// components/user-nav.tsx
"use client"

import { useContext } from "react"
import { useRouter } from "next/navigation"
import { OrganizationContext } from "@/contexts/organization"
import { useRoles } from "@/contexts/roles"
import { LogOut, Settings, SlidersHorizontal } from "lucide-react"
import { signOut, useSession } from "next-auth/react"
import { FormattedMessage } from "react-intl"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { LanguageToggle } from "@/components/language-toggler"
import { Credits } from "@/components/main-nav/credits"
import { ThemeToggle } from "@/components/theme-toggle"

import OrganizationSettings from "./organization-settings"

export function UserNav() {
  const { data: session } = useSession()

  const { selectedOrganization } = useContext(OrganizationContext)
  const router = useRouter()
  const { isAppOwner } = useRoles()

  if (!session) {
    return null
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8 border-2 shadow-sm">
            {session?.user.image ? (
              <AvatarImage
                src={session?.user.image}
                alt={session?.user?.email}
              />
            ) : (
              <AvatarImage
                src={`https://ui-avatars.com/api/?background=random&name=${session?.user?.email}`}
                alt={session?.user?.email}
              />
            )}
            <AvatarFallback>
              {session?.user?.email
                ? session.user.email
                    .split(" ")
                    .map((name) => name.charAt(0).toUpperCase())
                    .join("")
                : "42x"}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {session?.user?.name}
            </p>
            <p className="text-xs leading-none text-muted-foreground">
              {session?.user?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <span className="mr-2">
            <FormattedMessage id="credits" />:{" "}
          </span>
          <Credits />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {/* 
          <DropdownMenuItem>
            <User className="mr-2 h-4 w-4" />
            <span>Profile</span>
            <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
          </DropdownMenuItem>*/}
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => router.push("/settings")}>
            <Settings className="mr-2 h-4 w-4" />
            <span>
              <FormattedMessage id="settings" />
            </span>
            <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <div className="flex items-center justify-center space-x-4">
            <LanguageToggle />
            {/* <ThemeToggle /> */}
          </div>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        {isAppOwner() && (
          <DropdownMenuSub>
            <DropdownMenuItem onClick={() => router.push("/admin")}>
              <span className="flex">
                <SlidersHorizontal className="me-2 h-4 w-4" />
                SUPER ADMIN
              </span>
            </DropdownMenuItem>
          </DropdownMenuSub>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault()
            signOut()
          }}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>
            <FormattedMessage id="logOut" />
          </span>
          <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
